/**
 * 邮件任务管理页面组件。
 *
 * 该文件实现了 CRM 系统中的「邮件任务」功能页面，主要用于批量邮件发送任务的管理，
 * 包括任务的创建、启动、暂停、停止、删除以及发送记录查看。
 *
 * 主要导出内容：
 * - `CrmEmailTasksPage` — 注册为 `<crm-email-tasks-page>` 自定义元素的 Lit 组件类
 *
 * 与其他模块的关系：
 * - 通过 `../api.js` 调用 `emailTaskApi`、`emailTemplateApi`、`emailAccountApi`、`customerApi`、`categoryApi` 等 API 层接口
 * - 通过 `./email-tasks-utils.js` 中的 `buildCriteriaPayload` 构建条件筛选参数
 * - 依赖 `../utils/countries.js` 提供国家列表数据
 * - 依赖 `../components/` 下的公共组件（table-toolbar、loading-overlay、pagination、confirm-dialog、toast 等）
 *
 * 关键设计决策：
 * - 任务创建支持两种目标客户模式：手动勾选（manual）和按条件自动圈选（criteria）
 * - 发送节奏通过每日上限、发送时段、发送间隔等参数精细化控制
 * - 邮箱账户支持多选，以实现多账户轮发
 * - 弹窗支持 ESC 键关闭和点击遮罩关闭，使用 `_backdropMousedown` 防止拖拽选中时误触关闭
 */
import { css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { emailTaskApi, emailTemplateApi, emailAccountApi, customerApi, categoryApi } from '../api.js';
import { BaseElement } from '../base-element.js';
import type { EmailTask, EmailTemplate, EmailAccount, Customer, EmailSendRecord, CustomerCategory } from '../types.js';
import { COUNTRIES } from '../utils/countries.js';
import { toast } from '../components/toast.js';
import '../components/table-toolbar.js';
import '../components/loading-overlay.js';
import '../components/rich-editor.js';
import '../components/pagination.js';
import { showConfirm } from '../components/confirm-dialog.js';
import { isNetworkError } from '../utils/index.js';
import { buildCriteriaPayload } from './email-tasks-utils.js';

/**
 * 邮件任务状态标签映射表。
 *
 * key 为后端返回的状态码（number），value 包含：
 * - `label`：中文显示文本
 * - `cls`：Tailwind CSS 类名，用于控制徽章的背景色和文字色
 *
 * 状态码含义：
 * - 0：待开始（任务已创建但尚未启动）
 * - 1：进行中（任务正在执行发送）
 * - 2：已暂停（任务被手动暂停，可恢复）
 * - 3：已完成（任务所有邮件已发送完毕）
 * - 4：已停止（任务被终止，不可恢复）
 */
const STATUS_LABELS: Record<number, { label: string; cls: string }> = {
  0: { label: '待开始', cls: 'bg-bg text-text-secondary' },
  1: { label: '进行中', cls: 'bg-primary/10 text-primary' },
  2: { label: '已暂停', cls: 'bg-warning/10 text-warning' },
  3: { label: '已完成', cls: 'bg-success/10 text-success' },
  4: { label: '已停止', cls: 'bg-error/10 text-error' },
};

/**
 * 单封邮件发送状态标签映射表。
 *
 * 用于发送记录弹窗中每条记录的状态展示。
 *
 * 状态码含义：
 * - 0：待发送（尚未到达发送时机）
 * - 1：发送中（正在尝试发送）
 * - 2：已发送（发送成功）
 * - 3：发送失败（发送过程中出错）
 */
const SEND_STATUS_LABELS: Record<number, { label: string; cls: string }> = {
  0: { label: '待发送', cls: 'bg-bg text-text-secondary' },
  1: { label: '发送中', cls: 'bg-primary/10 text-primary' },
  2: { label: '已发送', cls: 'bg-success/10 text-success' },
  3: { label: '发送失败', cls: 'bg-error/10 text-error' },
};

/**
 * 筛选条件控件的类型枚举。
 * - `category-select`：客户分类下拉选择框（数据来源于 categoryApi）
 * - `country-select`：国家下拉选择框（数据来源于 COUNTRIES 常量，支持搜索）
 * - `text`：文本输入框（用于关键词搜索）
 * - `toggle`：开关切换（用于布尔条件，如"是否有往来邮件"）
 */
type CriterionType = 'category-select' | 'country-select' | 'text' | 'toggle';

/**
 * 筛选条件定义接口。
 * - `key`：条件的唯一标识，对应后端 API 的参数名
 * - `label`：在 UI 中显示的中文标签
 * - `type`：控件类型，决定渲染哪种输入控件
 */
interface CriterionDef {
  key: string;
  label: string;
  type: CriterionType;
}

/**
 * 筛选条件定义列表。
 *
 * 定义了任务创建弹窗中可用的客户筛选条件，顺序即为 UI 中的排列顺序。
 * 每个条件对应一种控件类型，由 `renderCriterionControl` 方法根据 `type` 分发渲染。
 */
const CRITERIA_DEFS: CriterionDef[] = [
  { key: 'category_id', label: '客户分类', type: 'category-select' },
  { key: 'nation_id', label: '国家', type: 'country-select' },
  { key: 'keyword', label: '关键词', type: 'text' },
  { key: 'has_email', label: '有往来邮件', type: 'toggle' },
];

/**
 * 邮件任务管理页面组件。
 *
 * 职责：
 * - 展示邮件任务列表（分页表格），支持启动、暂停、停止、删除操作
 * - 提供任务创建弹窗，支持手动选择客户或按条件自动圈选客户
 * - 管理任务关联的邮箱账户、邮件模板的选择
 * - 查看任务发送记录详情（分页弹窗）
 *
 * 状态管理：
 * - 列表数据：`tasks`、`total`、`currentPage`、`perPage`、`isLoading`
 * - 创建弹窗表单：`showModal`、`formTemplateId`、`targetMode`、`selectedAccountIds`、`selectedCustomerIds`
 * - 条件筛选相关：`criteriaCategoryId`、`criteriaNationId`、`criteriaKeyword`、`criteriaHasEmail`、`criteriaMatchTotal`
 * - 筛选后的客户列表：`filteredCustomers`、`filteredTotal`、`filteredPage`
 * - 发送记录弹窗：`showRecordsModal`、`records`、`recordsTotal`、`recordsPage`、`recordsTaskId`
 *
 * 该组件注册为 `<crm-email-tasks-page>` 自定义元素。
 */
@customElement('crm-email-tasks-page')
export class CrmEmailTasksPage extends BaseElement {
  /** 邮件任务列表数据 */
  @state() private tasks: EmailTask[] = [];
  /** 列表加载状态标志 */
  @state() private isLoading = false;
  /** 任务列表请求序号，仅允许最后一次分页请求更新页面。 */
  private _listRequestId = 0;
  /** 列表数据总条数（用于分页计算） */
  @state() private total = 0;
  /** 当前页码 */
  @state() private currentPage = 1;
  /** 每页显示条数 */
  @state() private perPage = 20;
  /** 是否显示创建任务弹窗 */
  @state() private showModal = false;
  /** 是否显示发送记录弹窗 */
  @state() private showRecordsModal = false;
  /** 发送记录列表数据 */
  @state() private records: EmailSendRecord[] = [];
  /** 发送记录总条数 */
  @state() private recordsTotal = 0;
  /** 发送记录当前页码 */
  @state() private recordsPage = 1;
  /** 当前查看发送记录的任务 ID */
  @state() private recordsTaskId = 0;
  /** 发送记录请求序号，仅允许最后一次任务或分页请求更新弹窗。 */
  private _recordsRequestId = 0;
  /** 可用邮件模板列表（仅加载启用状态的模板） */
  @state() private templates: EmailTemplate[] = [];
  /** 可用邮箱账户列表（仅加载启用状态的账户） */
  @state() private accounts: EmailAccount[] = [];
  /** 创建任务时选中的邮箱账户 ID 列表 */
  @state() private selectedAccountIds: number[] = [];
  /** 创建任务时手动选中的客户 ID 列表 */
  @state() private selectedCustomerIds: number[] = [];
  /** 创建任务表单中选中的模板 ID（字符串形式，用于 crm-select 绑定） */
  @state() private formTemplateId = '';
  /** 创建任务时编辑的正文 HTML */
  @state() private taskContent = '';
  /** 目标客户模式：manual=手动勾选客户，criteria=按条件自动圈选 */
  @state() private targetMode: 'manual' | 'criteria' = 'manual';
  /** 客户分类列表（用于筛选条件下拉选项） */
  @state() private categories: CustomerCategory[] = [];
  /** 条件筛选 - 选中的客户分类 ID（0 表示全部） */
  @state() private criteriaCategoryId = 0;
  /** 条件筛选 - 选中的国家 ID（0 表示全部） */
  @state() private criteriaNationId = 0;
  /** 条件筛选 - 关键词文本（公司名或联系人） */
  @state() private criteriaKeyword = '';
  /** 条件筛选 - 是否仅匹配有往来邮件的客户 */
  @state() private criteriaHasEmail = false;
  /** 条件模式下匹配到的客户总数（仅用于预览展示） */
  @state() private criteriaMatchTotal = 0;
  /** 手动模式下筛选出的客户列表（当前页） */
  @state() private filteredCustomers: Customer[] = [];
  /** 手动模式下筛选出的客户总数 */
  @state() private filteredTotal = 0;
  /** 手动模式下筛选客户的当前页码 */
  @state() private filteredPage = 1;
  /** 手动模式下筛选客户每页显示条数（固定值，非响应式状态） */
  private readonly filteredPerPage = 20;
  /** 标记鼠标是否在遮罩层上按下（用于区分点击遮罩关闭与拖拽选择，防止误触关闭弹窗） */
  private _backdropMousedown = false;

  static styles = css`
    :host { display: block; height: 100%; }
  `;

  /**
   * 组件挂载时的生命周期回调。
   *
   * 调用父类方法完成基础初始化后：
   * 1. 加载邮件任务列表数据
   * 2. 在 document 上注册 ESC 键监听（用于关闭弹窗）
   */
  connectedCallback() {
    super.connectedCallback();
    this.loadTasks();
    document.addEventListener('keydown', this.onModalKeydown);
  }

  /**
   * 组件卸载时的生命周期回调。
   *
   * 调用父类方法后，移除 document 上的 ESC 键监听，防止内存泄漏。
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.onModalKeydown);
  }

  /**
   * 加载邮件任务列表数据。
   *
   * 根据当前页码和每页条数从后端获取任务列表，更新 tasks、total、currentPage、perPage 状态。
   * 加载期间设置 isLoading 为 true 以显示加载遮罩。
   *
   * @returns 无返回值；失败时仅打印错误日志，不抛出异常
   */
  private async loadTasks() {
    const requestId = ++this._listRequestId;
    this.isLoading = true;
    try {
      const response = await emailTaskApi.list({ page: this.currentPage, per_page: this.perPage });
      if (requestId !== this._listRequestId) return;
      this.tasks = response.data;
      this.total = response.pagination.total;
      this.currentPage = response.pagination.current_page;
      this.perPage = response.pagination.per_page;
    } catch { if (requestId === this._listRequestId) console.error('Failed to load tasks'); }
    finally { if (requestId === this._listRequestId) this.isLoading = false; }
  }

  /**
   * 加载可用邮件模板列表。
   *
   * 请求参数 `status: 1` 表示仅加载启用状态的模板。
   * `per_page: 100` 表示一次性加载较多条目，避免分页。
   * 结果存入 templates 状态。
   */
  private async loadTemplates() {
    try {
      const res = await emailTemplateApi.list({ per_page: 100, status: 1 });
      this.templates = res.data;
    } catch { console.error('Failed to load templates'); }
  }

  /**
   * 加载可用邮箱账户列表。
   *
   * 请求 `per_page: 100` 获取全部账户，然后在前端过滤 `status === 1`（启用状态）的账户。
   * 结果存入 accounts 状态。
   */
  private async loadAccounts() {
    try {
      const res = await emailAccountApi.list({ per_page: 100 });
      this.accounts = res.data.filter(a => a.status === 1);
    } catch { console.error('Failed to load accounts'); }
  }

  /**
   * 切换目标客户模式。
   *
   * 切换模式时会重置所有已选客户、筛选条件和筛选结果状态，
   * 确保两种模式之间互不干扰。
   *
   * @param mode - 目标模式：'manual' 手动勾选 / 'criteria' 按条件圈选
   */
  private setTargetMode(mode: 'manual' | 'criteria') {
    this.targetMode = mode;
    this.selectedCustomerIds = [];
    this.criteriaCategoryId = 0;
    this.criteriaNationId = 0;
    this.criteriaKeyword = '';
    this.criteriaHasEmail = false;
    this.criteriaMatchTotal = 0;
    this.filteredCustomers = [];
    this.filteredTotal = 0;
    this.filteredPage = 1;
  }

  /**
   * 构建筛选条件参数对象。
   *
   * 委托给 `email-tasks-utils.js` 中的 `buildCriteriaPayload` 函数，
   * 将组件状态中的筛选条件转换为后端 API 可接受的参数格式。
   * 该函数会自动过滤掉值为 0 或空的无效条件。
   *
   * @returns 筛选条件参数对象，可能包含 category_id、nation_id、keyword、has_email 等字段
   */
  private buildCriteriaParams() {
    return buildCriteriaPayload({
      criteriaCategoryId: this.criteriaCategoryId,
      criteriaNationId: this.criteriaNationId,
      criteriaKeyword: this.criteriaKeyword,
      criteriaHasEmail: this.criteriaHasEmail,
    });
  }

  /**
   * 按当前筛选条件查询客户列表（用于手动模式下的客户预览）。
   *
   * 根据筛选条件和当前页码从后端加载客户数据，更新 filteredCustomers 和 filteredTotal。
   * 失败时清空筛选结果并打印错误日志。
   */
  private async applyCustomerFilter() {
    const params = this.buildCriteriaParams();
    try {
      const res = await customerApi.list({
        ...params,
        page: this.filteredPage,
        per_page: this.filteredPerPage,
      });
      this.filteredCustomers = res.data;
      this.filteredTotal = res.pagination.total;
    } catch (err: any) {
      console.error('Failed to apply filter', err);
      this.filteredCustomers = [];
      this.filteredTotal = 0;
    }
  }

  /**
   * 刷新条件模式下匹配到的客户总数。
   *
   * 在条件模式中，每次筛选条件变化时调用，请求 per_page=1 以获取总数而不加载具体数据，
   * 用于在 UI 中实时预览匹配客户数。仅在 targetMode === 'criteria' 时执行。
   */
  private async refreshCriteriaMatchTotal() {
    if (this.targetMode !== 'criteria') return;
    const params = this.buildCriteriaParams();
    try {
      const res = await customerApi.list({ ...params, page: 1, per_page: 1 });
      this.criteriaMatchTotal = res.pagination.total;
    } catch {
      this.criteriaMatchTotal = 0;
    }
  }

  /**
   * 手动模式下筛选客户列表的翻页处理。
   *
   * 更新页码后重新调用 applyCustomerFilter 加载新页数据。
   *
   * @param page - 目标页码
   */
  private onFilterPageChange(page: number) {
    this.filteredPage = page;
    this.applyCustomerFilter();
  }

  /**
   * 任务列表的翻页处理。
   *
   * 更新页码后重新调用 loadTasks 加载新页数据。
   *
   * @param page - 目标页码
   */
  private handlePageChange(page: number) { this.currentPage = page; this.loadTasks(); }

  /**
   * 打开创建任务弹窗。
   *
   * 重置所有表单状态为初始值（包括选中的账户、客户、模板、筛选条件等），
   * 然后设置 showModal 为 true 显示弹窗，并异步加载表单所需的模板、账户、分类数据。
   */
  private openCreateModal() {
    this.selectedAccountIds = [];
    this.selectedCustomerIds = [];
    this.formTemplateId = '';
    this.taskContent = '';
    this.targetMode = 'manual';
    this.criteriaCategoryId = 0;
    this.criteriaNationId = 0;
    this.criteriaKeyword = '';
    this.criteriaHasEmail = false;
    this.criteriaMatchTotal = 0;
    this.filteredCustomers = [];
    this.filteredTotal = 0;
    this.filteredPage = 1;
    this.showModal = true;
    this.loadFormData();
  }

  /** 关闭创建任务弹窗 */
  private closeCreateModal() { this.showModal = false; }

  /**
   * 并行加载创建任务表单所需的依赖数据。
   *
   * 使用 Promise.all 同时请求模板列表、邮箱账户列表和客户分类列表，
   * 减少串行等待时间。
   */
  private async loadFormData() {
    await Promise.all([
      this.loadTemplates(),
      this.loadAccounts(),
      this.loadCategories(),
    ]);
  }

  /**
   * 加载客户分类列表。
   *
   * 从 categoryApi 获取全部分类数据，存入 categories 状态，
   * 用于条件筛选的下拉选项。
   */
  private async loadCategories() {
    try {
      this.categories = await categoryApi.list();
    } catch { console.error('Failed to load categories'); }
  }

  /**
   * 处理创建任务表单提交。
   *
   * 从表单中收集任务名称、发送参数（每日上限、发送时段、间隔等），
   * 并根据目标模式（manual/criteria）组装不同的客户数据：
   * - manual 模式：附带 customer_ids 数组
   * - criteria 模式：附带 criteria 条件对象，后端负责圈选客户
   *
   * 提交前校验：至少选择一个邮箱账户；手动模式下至少选择一个客户；条件模式下至少设置一个筛选条件。
   * 成功后关闭弹窗并重新加载任务列表。
   *
   * @param e - 表单提交事件对象
   */
  private async handleSubmit(e: Event) {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);

    // 校验：至少选择一个邮箱账户
    if (this.selectedAccountIds.length === 0) { toast.error('请至少选择一个邮箱账户'); return; }

    // 组装基础任务参数（不含目标客户信息）
    const base = {
      task_name: formData.get('task_name') as string,
      template_id: Number(this.formTemplateId),
      content: (formData.get('content') as string) || this.taskContent,
      account_ids: [...this.selectedAccountIds],
      daily_limit: Number(formData.get('daily_limit')) || 100,
      start_hour: Number(formData.get('start_hour')) || 8,
      end_hour: Number(formData.get('end_hour')) || 18,
      min_interval: Number(formData.get('min_interval')) || 60,
      max_interval: Number(formData.get('max_interval')) || 300,
    };

    // 根据目标模式组装不同的客户数据
    let data: any;
    if (this.targetMode === 'manual') {
      // 手动模式：必须选择至少一个客户
      if (this.selectedCustomerIds.length === 0) { toast.error('请至少选择一个客户'); return; }
      data = { ...base, target_mode: 'manual', customer_ids: [...this.selectedCustomerIds] };
    } else {
      // 条件模式：必须设置至少一个筛选条件
      const criteria = this.buildCriteriaParams();
      if (Object.keys(criteria).length === 0) { toast.error('请至少设置一个筛选条件'); return; }
      data = { ...base, target_mode: 'criteria', criteria };
    }

    try {
      await emailTaskApi.create(data);
      toast.success('任务创建成功');
      await this.loadTasks();
      this.closeCreateModal();
    } catch (err: any) {
      // 网络错误不显示 toast（由全局拦截器处理），其他错误显示具体信息
      if (!isNetworkError(err.message)) toast.error(err.message || '创建失败');
    }
  }

  /**
   * 删除指定邮件任务。
   *
   * 弹出确认对话框，用户确认后调用 API 删除任务。
   * 仅对待开始（status=0）状态的任务显示删除按钮。
   *
   * @param id - 任务 ID
   */
  private async deleteTask(id: number) {
    if (!(await showConfirm('确定要删除此任务吗？'))) return;
    try {
      await emailTaskApi.delete(id);
      toast.success('任务删除成功');
      await this.loadTasks();
    } catch (err: any) {
      if (!isNetworkError(err.message)) toast.error(err.message || '删除失败');
    }
  }

  /**
   * 启动指定邮件任务。
   *
   * 将待开始（0）或已暂停（2）状态的任务切换为进行中。
   *
   * @param id - 任务 ID
   */
  private async startTask(id: number) {
    try {
      await emailTaskApi.start(id);
      toast.success('任务已启动');
      await this.loadTasks();
    } catch (err: any) {
      if (!isNetworkError(err.message)) toast.error(err.message || '启动失败');
    }
  }

  /**
   * 暂停指定邮件任务。
   *
   * 将进行中（1）状态的任务切换为已暂停。暂停后可通过 startTask 恢复。
   *
   * @param id - 任务 ID
   */
  private async pauseTask(id: number) {
    try {
      await emailTaskApi.pause(id);
      toast.success('任务已暂停');
      await this.loadTasks();
    } catch (err: any) {
      if (!isNetworkError(err.message)) toast.error(err.message || '暂停失败');
    }
  }

  /**
   * 停止指定邮件任务。
   *
   * 将进行中（1）或已暂停（2）状态的任务终止。停止后不可恢复。
   * 弹出确认对话框，提示用户停止操作不可逆。
   *
   * @param id - 任务 ID
   */
  private async stopTask(id: number) {
    if (!(await showConfirm('停止后无法恢复，确定要停止此任务吗？'))) return;
    try {
      await emailTaskApi.stop(id);
      toast.success('任务已停止');
      await this.loadTasks();
    } catch (err: any) {
      if (!isNetworkError(err.message)) toast.error(err.message || '停止失败');
    }
  }

  /**
   * 打开发送记录弹窗。
   *
   * 记录当前任务 ID，重置页码为 1，显示弹窗后加载发送记录数据。
   *
   * @param taskId - 要查看的任务 ID
   */
  private async viewRecords(taskId: number) {
    this.recordsTaskId = taskId;
    this.recordsPage = 1;
    this.showRecordsModal = true;
    await this.loadRecords();
  }

  /**
   * 加载发送记录数据。
   *
   * 根据当前任务 ID 和页码从后端获取发送记录列表，每页 20 条。
   * 结果存入 records 和 recordsTotal 状态。
   */
  private async loadRecords() {
    const requestId = ++this._recordsRequestId;
    try {
      const res = await emailTaskApi.getSendRecords(this.recordsTaskId, { page: this.recordsPage, per_page: 20 });
      if (requestId !== this._recordsRequestId) return;
      this.records = res.data;
      this.recordsTotal = res.pagination.total;
    } catch { if (requestId === this._recordsRequestId) console.error('Failed to load send records'); }
  }

  /** 关闭发送记录弹窗并清空记录数据 */
  private closeRecordsModal() { this._recordsRequestId++; this.showRecordsModal = false; this.records = []; }

  /**
   * ESC 键监听回调（箭头函数，确保 this 绑定正确）。
   *
   * 按 ESC 键时优先关闭发送记录弹窗，其次关闭创建任务弹窗。
   *
   * @param e - 键盘事件对象
   */
  private onModalKeydown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (this.showRecordsModal) this.closeRecordsModal();
      else if (this.showModal) this.closeCreateModal();
    }
  }

  /**
   * 切换邮箱账户的选中状态。
   *
   * 若已选中则取消，未选中则添加。切换后调用 requestUpdate 触发重渲染。
   *
   * @param id - 邮箱账户 ID
   */
  private toggleAccount(id: number) {
    this.selectedAccountIds = this.selectedAccountIds.includes(id)
      ? this.selectedAccountIds.filter(x => x !== id)
      : [...this.selectedAccountIds, id];
    this.requestUpdate();
  }

  /**
   * 切换客户的选中状态。
   *
   * 若已选中则取消，未选中则添加。切换后调用 requestUpdate 触发重渲染。
   *
   * @param id - 客户 ID
   */
  private toggleCustomer(id: number) {
    this.selectedCustomerIds = this.selectedCustomerIds.includes(id)
      ? this.selectedCustomerIds.filter(x => x !== id)
      : [...this.selectedCustomerIds, id];
    this.requestUpdate();
  }

  /** 全选所有可用邮箱账户 */
  private selectAllAccounts() {
    this.selectedAccountIds = this.accounts.map(a => a.id);
    this.requestUpdate();
  }

  /** 取消选中所有邮箱账户 */
  private deselectAllAccounts() {
    this.selectedAccountIds = [];
    this.requestUpdate();
  }

  /**
   * 根据模板 ID 获取模板名称。
   *
   * @param id - 模板 ID
   * @returns 模板名称；若未找到则返回 `模板 #${id}` 作为兜底显示
   */
  private getTemplateName(id: number) { return this.templates.find(t => t.id === id)?.name || `模板 #${id}`; }

  /**
   * 渲染任务状态徽章。
   *
   * 根据状态码从 STATUS_LABELS 查找对应的标签和样式类名。
   * 未知状态码显示为"未知"并使用灰色样式。
   *
   * @param status - 任务状态码
   * @returns Lit html 模板，包含状态徽章的 span 元素
   */
  private renderStatusBadge(status: number) {
    const s = STATUS_LABELS[status] || { label: '未知', cls: 'bg-bg text-text-muted' };
    return html`<span class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${s.cls}">${s.label}</span>`;
  }

  /**
   * 渲染发送记录弹窗。
   *
   * 弹窗包含一个表格，展示每条发送记录的收件人、主题、状态和发送时间。
   * 支持点击遮罩关闭（使用 _backdropMousedown 防止拖拽误触）。
   * 底部包含分页组件。
   *
   * @returns Lit html 模板，包含遮罩层、弹窗容器、记录表格和分页
   */
  private renderRecordsModal() {
    return html`
      <div class="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-center z-50" @mousedown=${(e: Event) => { this._backdropMousedown = e.target === e.currentTarget; }} @click=${() => { if (this._backdropMousedown) this.closeRecordsModal(); }}>
        <div class="bg-surface rounded-md shadow-xl w-full max-w-5xl max-h-[85vh] overflow-y-auto border border-border mx-4" @click=${(e: Event) => e.stopPropagation()}>
          <div class="sticky top-0 bg-surface flex justify-between items-center px-6 pt-5 pb-4 border-b border-border">
            <h2 class="m-0 font-semibold text-xl text-text">发送记录 (共 ${this.recordsTotal} 条)</h2>
            <button class="p-1.5 rounded hover:bg-bg cursor-pointer text-text-muted transition-colors" @click=${this.closeRecordsModal}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
            </button>
          </div>
          <div class="p-6">
            <table class="w-full">
              <thead>
                <tr class="border-b border-border">
                  <th class="px-3 py-2 text-left text-xs font-medium text-text-muted uppercase">收件人</th>
                  <th class="px-3 py-2 text-left text-xs font-medium text-text-muted uppercase">主题</th>
                  <th class="px-3 py-2 text-left text-xs font-medium text-text-muted uppercase">状态</th>
                  <th class="px-3 py-2 text-left text-xs font-medium text-text-muted uppercase">发送时间</th>
                </tr>
              </thead>
              <tbody>
                ${this.records.length === 0
                  ? html`<tr><td colspan="4" class="text-center py-8 text-text-muted">暂无发送记录</td></tr>`
                  : this.records.map(r => html`
                    <tr class="hover:bg-bg/50 transition-colors border-b border-border">
                      <td class="px-3 py-2 text-sm text-text">${r.send_to}</td>
                      <td class="px-3 py-2 text-sm text-text truncate max-w-[200px]">${r.subject}</td>
                      <td class="px-3 py-2">
                        ${(() => { const s = SEND_STATUS_LABELS[r.send_status] || { label: '未知', cls: 'bg-bg text-text-muted' }; return html`<span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${s.cls}">${s.label}</span>`; })()}
                        ${r.error_message ? html`<span class="ml-1 text-xs text-error" title=${r.error_message}>失败</span>` : ''}
                      </td>
                      <!-- sent_time 为 Unix 时间戳（秒），需 *1000 转为毫秒后格式化为中文日期 -->
                      <td class="px-3 py-2 text-xs text-text-muted">${r.sent_time ? new Date(r.sent_time * 1000).toLocaleString('zh-CN') : '-'}</td>
                    </tr>
                  `)}
              </tbody>
            </table>
            <crm-pagination
              .current=${this.recordsPage}
              .perPage=${20}
              .total=${this.recordsTotal}
              .showTotal=${false}
              @page-change=${(e: CustomEvent) => { this.recordsPage = e.detail.page; this.loadRecords(); }}
            ></crm-pagination>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * 主渲染方法。
   *
   * 页面结构自上而下：
   * 1. 顶部操作栏：页面标题、新建任务按钮、工具栏（含刷新）
   * 2. 任务列表表格：展示任务名称、模板、客户数、进度（已发送/总数/失败数）、状态徽章和操作按钮
   *    - 操作按钮根据任务状态动态显示：启动（待开始/暂停）、暂停（进行中）、停止（进行中/暂停）、记录（始终）、删除（仅待开始）
   * 3. 分页组件
   * 4. 条件渲染的创建任务弹窗和发送记录弹窗
   *
   * @returns Lit html 模板
   */
  render() {
    return html`
      <div class="flex flex-col h-full">
        <div class="flex items-center gap-3 mb-3 flex-shrink-0 flex-wrap">
          <span class="text-base font-semibold text-text">邮件任务管理</span>
          <div class="flex-1"></div>
          <button class="bg-primary hover:bg-primary-hover text-white px-4 py-1.5 rounded text-sm font-medium transition-colors cursor-pointer shadow-sm shadow-primary/20 flex items-center gap-2" @click=${this.openCreateModal}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>新建任务
          </button>
          <crm-table-toolbar .total=${this.total} .label=${'个任务'} .loading=${this.isLoading} @crm-toolbar-refresh=${() => this.loadTasks()}></crm-table-toolbar>
        </div>

        <div class="flex-1 min-h-0 flex flex-col">
          <div class="bg-surface rounded-md shadow-lg flex flex-col flex-1 min-h-0 overflow-hidden relative">
            <crm-loading-overlay .active=${this.isLoading}></crm-loading-overlay>
            <div class="overflow-auto flex-1 min-h-0">
              <table class="w-full">
                <thead>
                  <tr class="border-b border-border">
                    <th class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">任务名称</th>
                    <th class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">模板</th>
                    <th class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">客户数</th>
                    <th class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">进度</th>
                    <th class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">状态</th>
                    <th class="px-4 py-2.5 text-center text-xs font-medium text-text-secondary uppercase tracking-wider">操作</th>
                  </tr>
                </thead>
                <tbody>
                  ${!this.isLoading && this.tasks.length === 0
                    ? html`<tr><td colspan="6" class="text-center py-12 text-text-muted border-b border-border">暂无邮件任务</td></tr>`
                    : this.tasks.map(t => html`
                      <tr class="hover:bg-bg/50 transition-colors border-b border-border">
                        <td class="px-4 py-2 text-sm font-medium text-text">${t.task_name}</td>
                        <td class="px-4 py-2 text-sm text-text">${this.getTemplateName(t.template_id)}</td>
                        <td class="px-4 py-2 text-sm text-text">${t.total_customers}</td>
                        <td class="px-4 py-2 text-sm text-text">
                          <span class="text-success">${t.sent_count}</span>/<span class="text-text-muted">${t.total_customers}</span>
                          ${t.failed_count > 0 ? html` (<span class="text-error">${t.failed_count}</span>失败)` : ''}
                        </td>
                        <td class="px-4 py-2">${this.renderStatusBadge(t.status)}</td>
                        <td class="px-4 py-2 flex gap-1.5 flex-wrap">
                          ${t.status === 0 || t.status === 2 ? html`<button class="text-success hover:text-success-hover text-xs font-medium px-2.5 py-1.5 rounded transition-colors cursor-pointer" @click=${() => this.startTask(t.id)}>启动</button>` : ''}
                          ${t.status === 1 ? html`<button class="text-warning hover:text-warning-hover text-xs font-medium px-2.5 py-1.5 rounded transition-colors cursor-pointer" @click=${() => this.pauseTask(t.id)}>暂停</button>` : ''}
                          ${t.status === 1 || t.status === 2 ? html`<button class="text-error hover:text-error-hover text-xs font-medium px-2.5 py-1.5 rounded transition-colors cursor-pointer" @click=${() => this.stopTask(t.id)}>停止</button>` : ''}
                          <button class="text-primary hover:text-primary-hover text-xs font-medium px-2.5 py-1.5 rounded transition-colors cursor-pointer" @click=${() => this.viewRecords(t.id)}>记录</button>
                          ${t.status === 0 ? html`<button class="text-error hover:text-error-hover text-xs font-medium px-2.5 py-1.5 rounded transition-colors cursor-pointer" @click=${() => this.deleteTask(t.id)}>删除</button>` : ''}
                        </td>
                      </tr>
                    `)}
                </tbody>
              </table>
            </div>
            <crm-pagination
              .current=${this.currentPage}
              .perPage=${this.perPage}
              .total=${this.total}
              @page-change=${(e: CustomEvent) => this.handlePageChange(e.detail.page)}
            ></crm-pagination>
          </div>
        </div>
      </div>

      ${this.showModal ? this.renderCreateModal() : ''}
      ${this.showRecordsModal ? this.renderRecordsModal() : ''}
    `;
  }

  /**
   * 根据筛选条件定义渲染对应的输入控件。
   *
   * 根据 `def.type` 分发渲染：
   * - `category-select`：渲染分类下拉框，数据来源为 categories 状态
   * - `country-select`：渲染国家下拉框，数据来源为 COUNTRIES 常量，支持搜索
   * - `text`：渲染文本输入框，用于关键词搜索
   * - `toggle`：渲染复选框开关，用于布尔条件
   *
   * 每个控件在值变化时都会触发 refreshCriteriaMatchTotal 更新匹配数预览。
   *
   * @param def - 筛选条件定义
   * @returns 对应的 Lit html 控件模板
   */
  private renderCriterionControl(def: CriterionDef) {
    switch (def.type) {
      case 'category-select':
        return html`
          <crm-select
            fullWidth
            .value=${String(this.criteriaCategoryId)}
            .options=${[
              { label: '全部分类', value: '0' },
              ...this.categories.map(c => ({ label: c.name, value: String(c.id) })),
            ]}
            @change=${(e: CustomEvent) => {
              this.criteriaCategoryId = parseInt(e.detail.value) || 0;
              this.refreshCriteriaMatchTotal();
            }}
          ></crm-select>
        `;
      case 'country-select':
        return html`
          <crm-select
            fullWidth
            searchable
            .value=${String(this.criteriaNationId)}
            .options=${[
              { label: '全部国家', value: '0' },
              ...COUNTRIES.map(c => ({ label: c.name, value: String(c.id) })),
            ]}
            @change=${(e: CustomEvent) => {
              this.criteriaNationId = parseInt(e.detail.value) || 0;
              this.refreshCriteriaMatchTotal();
            }}
          ></crm-select>
        `;
      case 'text':
        return html`
          <input
            type="text"
            class="w-full rounded border border-border bg-bg px-3 py-1.5 text-sm"
            .value=${this.criteriaKeyword}
            placeholder="公司名或联系人"
            @input=${(e: Event) => {
              this.criteriaKeyword = (e.target as HTMLInputElement).value;
            }}
            @change=${() => this.refreshCriteriaMatchTotal()}
          />
        `;
      case 'toggle':
        return html`
          <label class="inline-flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              class="rounded"
              .checked=${this.criteriaHasEmail}
              @change=${(e: Event) => {
                this.criteriaHasEmail = (e.target as HTMLInputElement).checked;
                this.refreshCriteriaMatchTotal();
              }}
            />
            仅匹配有往来邮件的客户
          </label>
        `;
      default:
        throw new Error(`unknown criterion control type: ${(def as CriterionDef).type}`);
    }
  }

  /**
   * 渲染目标客户模式切换的单选按钮组。
   *
   * 提供两个按钮：「手动勾选客户」和「按条件自动圈选」。
   * 选中状态使用 primary 色高亮，未选中使用灰色边框样式。
   *
   * @returns Lit html 模板，包含两个模式切换按钮
   */
  private renderTargetModeRadio() {
    const base = 'px-3 py-1.5 rounded text-sm cursor-pointer border transition-colors';
    const active = 'bg-primary text-white border-primary';
    const inactive = 'bg-bg text-text-secondary border-border hover:bg-surface';
    return html`
      <div class="inline-flex gap-2">
        <button type="button"
          class="${base} ${this.targetMode === 'manual' ? active : inactive}"
          @click=${() => this.setTargetMode('manual')}>手动勾选客户</button>
        <button type="button"
          class="${base} ${this.targetMode === 'criteria' ? active : inactive}"
          @click=${() => this.setTargetMode('criteria')}>按条件自动圈选</button>
      </div>
    `;
  }

  /**
   * 渲染手动模式下的客户选择面板。
   *
   * 包含以下区块：
   * - 统计信息：匹配总数和已选数量，提供「全选本页」和「清空」快捷操作
   * - 客户列表：可滚动区域，每项显示客户名称和公司名，支持勾选
   * - 分页：筛选结果的分页导航
   *
   * @returns Lit html 模板，包含客户选择面板
   */
  private renderManualCustomerPanel() {
    return html`
      <div class="space-y-2">
        <div class="flex items-center justify-between">
          <span class="text-sm text-text-secondary">
            共 ${this.filteredTotal} 个匹配客户，已选 ${this.selectedCustomerIds.length}
          </span>
          <div class="flex gap-2">
            <button type="button" class="text-xs text-primary underline"
              @click=${() => { this.selectedCustomerIds = this.filteredCustomers.map(c => c.id); this.requestUpdate(); }}>
              全选本页</button>
            <button type="button" class="text-xs text-text-secondary underline"
              @click=${() => { this.selectedCustomerIds = []; this.requestUpdate(); }}>
              清空</button>
          </div>
        </div>
        <div class="max-h-[18vh] overflow-y-auto border border-border rounded p-2 space-y-1">
          ${this.filteredCustomers.length === 0
            ? html`<div class="text-xs text-text-muted py-4 text-center">点击"筛选"加载客户</div>`
            : this.filteredCustomers.map(c => html`
                <label class="flex items-center gap-2 px-2 py-1 rounded hover:bg-bg cursor-pointer text-sm">
                  <input type="checkbox"
                    .checked=${this.selectedCustomerIds.includes(c.id)}
                    @change=${() => this.toggleCustomer(c.id)} />
                  <span>${c.name || c.company_name || `客户 #${c.id}`}</span>
                  ${c.company_name && c.name
                    ? html`<span class="text-text-muted">(${c.company_name})</span>` : ''}
                </label>
              `)}
        </div>
        <crm-pagination
          .current=${this.filteredPage}
          .perPage=${this.filteredPerPage}
          .total=${this.filteredTotal}
          .showTotal=${false}
          @page-change=${(e: CustomEvent) => this.onFilterPageChange(e.detail.page)}
        ></crm-pagination>
      </div>
    `;
  }

  /**
   * 渲染条件模式下的匹配数预览面板。
   *
   * 显示当前筛选条件匹配到的客户总数，以及提交后将按条件一次性圈选的提示。
   * 匹配数由 refreshCriteriaMatchTotal 异步更新。
   *
   * @returns Lit html 模板，包含匹配数预览卡片
   */
  private renderCriteriaPreview() {
    return html`
      <div class="rounded-md border border-border bg-bg p-6 text-center">
        <div class="text-text-muted text-sm">匹配客户数</div>
        <div class="text-4xl font-semibold text-primary mt-2">${this.criteriaMatchTotal}</div>
        <div class="text-xs text-text-muted mt-2">提交后按当前条件一次性圈选</div>
      </div>
    `;
  }

  /**
   * 渲染目标客户区块。
   *
   * 该区块包含：
   * - 模式切换按钮组（手动勾选 / 按条件圈选）
   * - 筛选条件控件网格（分类、国家、关键词、有往来邮件）
   * - 手动模式下的「筛选」按钮
   * - 根据模式渲染手动客户选择面板或条件匹配数预览
   *
   * @returns Lit html 模板，包含目标客户选择区域
   */
  private renderCustomerSection() {
    return html`
      <div class="space-y-3">
        <div>
          <label class="block text-sm font-medium mb-2">目标客户</label>
          ${this.renderTargetModeRadio()}
        </div>

        <div class="space-y-3 border border-border rounded-md p-3 bg-bg/50">
          ${CRITERIA_DEFS.map(def => html`
            <div class="grid grid-cols-[100px_1fr] items-center gap-2">
              <span class="text-sm text-text-secondary">${def.label}</span>
              ${this.renderCriterionControl(def)}
            </div>
          `)}
          ${this.targetMode === 'manual'
            ? html`<button type="button"
                class="bg-primary text-white px-3 py-1.5 rounded text-sm"
                @click=${() => { this.filteredPage = 1; this.applyCustomerFilter(); }}>
                筛选</button>`
            : ''}
        </div>

        ${this.targetMode === 'manual' ? this.renderManualCustomerPanel() : this.renderCriteriaPreview()}
      </div>
    `;
  }

  /**
   * 渲染创建任务弹窗。
   *
   * 弹窗采用全屏式布局（98vw x 98vh），内部分为两栏：
   * - 左栏（560px 固定宽）：任务名称输入、邮件模板选择、邮箱账户多选列表
   * - 右栏：目标客户区块（模式切换 + 筛选条件 + 客户列表/匹配预览）
   * - 底部独立区：发送参数网格（每日上限、发送时段起止、最小/最大间隔）
   * - 最底部：取消和创建任务按钮
   *
   * 支持点击遮罩关闭（使用 _backdropMousedown 防止拖拽误触）。
   *
   * @returns Lit html 模板，包含遮罩层和弹窗表单
   */
  private renderCreateModal() {
    return html`
      <div class="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-center z-50" @mousedown=${(e: Event) => { this._backdropMousedown = e.target === e.currentTarget; }} @click=${() => { if (this._backdropMousedown) this.closeCreateModal(); }}>
        <div class="bg-surface rounded-md shadow-xl w-full max-w-[98vw] h-[98vh] max-h-[98vh] overflow-hidden border border-border mx-4 flex flex-col" @click=${(e: Event) => e.stopPropagation()}>
          <div class="sticky top-0 bg-surface flex justify-between items-center px-6 pt-5 pb-4 border-b border-border">
            <h2 class="m-0 font-semibold text-xl text-text">新建邮件任务</h2>
            <button class="p-1.5 rounded hover:bg-bg cursor-pointer text-text-muted transition-colors" @click=${this.closeCreateModal}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
            </button>
          </div>
          <form @submit=${this.handleSubmit} class="flex flex-col flex-1 overflow-hidden">
            <div class="overflow-y-auto flex-1 p-6 space-y-4">
              <div class="grid grid-cols-[560px_1fr] gap-6 items-start">
                <div class="space-y-4">
                  <div>
                    <label class="block mb-1.5 text-sm font-medium text-text">任务名称 *</label>
                    <input type="text" name="task_name" required maxlength="256" class="w-full px-3.5 py-1.5 border border-border rounded text-sm bg-surface text-text focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all" />
                  </div>
                  <div>
                    <crm-select label="邮件模板 *" fullWidth .value=${this.formTemplateId} .options=${this.templates.map(t => ({ label: t.name, value: String(t.id) }))} @change=${(e: CustomEvent) => {
                      this.formTemplateId = e.detail.value;
                      const template = this.templates.find((item) => item.id === Number(e.detail.value));
                      this.taskContent = template?.content || '';
                    }}></crm-select>
                  </div>
                  <div>
                    <label class="block mb-1.5 text-sm font-medium text-text">任务邮件正文</label>
                    <crm-rich-editor name="content" .value=${this.taskContent}></crm-rich-editor>
                  </div>
                  <div>
                    <div class="flex justify-between items-center mb-1.5">
                      <label class="text-sm font-medium text-text">邮箱账户 * <span class="text-text-muted">（已选 ${this.selectedAccountIds.length} 个）</span></label>
                      <div class="flex gap-1.5">
                        <button type="button" class="text-xs text-primary hover:text-primary-hover cursor-pointer" @click=${this.selectAllAccounts}>全选</button>
                        <button type="button" class="text-xs text-primary hover:text-primary-hover cursor-pointer" @click=${this.deselectAllAccounts}>取消</button>
                      </div>
                    </div>
                    <div class="border border-border rounded-md max-h-52 overflow-y-auto bg-surface">
                      ${this.accounts.length === 0 ? html`<div class="px-3 py-2 text-sm text-text-muted">暂无邮箱账户</div>` : ''}
                      ${this.accounts.map(a => html`
                        <div class="flex items-center gap-2 px-3 py-2 hover:bg-bg/50 cursor-pointer transition-colors" @click=${() => this.toggleAccount(a.id)}>
                          <input type="checkbox" .checked=${this.selectedAccountIds.includes(a.id)} class="rounded cursor-pointer" @click=${(e: Event) => e.stopPropagation()} />
                          <span class="text-sm text-text">${a.address}</span>
                        </div>
                      `)}
                    </div>
                  </div>
                </div>
                <div class="space-y-4">
                  ${this.renderCustomerSection()}
                </div>
              </div>

              <div class="border-t border-border grid grid-cols-5 gap-4 pt-4">
                <div>
                  <label class="block mb-1.5 text-sm font-medium text-text">每日每账户发送上限</label>
                  <input type="number" name="daily_limit" min="1" max="1000" value="100" class="w-full px-3.5 py-1.5 border border-border rounded text-sm bg-surface text-text focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all" />
                </div>
                <div>
                  <label class="block mb-1.5 text-sm font-medium text-text">发送时段（起）</label>
                  <input type="number" name="start_hour" min="0" max="23" value="8" class="w-full px-3.5 py-1.5 border border-border rounded text-sm bg-surface text-text focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all" />
                </div>
                <div>
                  <label class="block mb-1.5 text-sm font-medium text-text">发送时段（止）</label>
                  <input type="number" name="end_hour" min="0" max="23" value="18" class="w-full px-3.5 py-1.5 border border-border rounded text-sm bg-surface text-text focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all" />
                </div>
                <div>
                  <label class="block mb-1.5 text-sm font-medium text-text">最小间隔（秒）</label>
                  <input type="number" name="min_interval" min="10" value="60" class="w-full px-3.5 py-1.5 border border-border rounded text-sm bg-surface text-text focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all" />
                </div>
                <div>
                  <label class="block mb-1.5 text-sm font-medium text-text">最大间隔（秒）</label>
                  <input type="number" name="max_interval" min="10" value="300" class="w-full px-3.5 py-1.5 border border-border rounded text-sm bg-surface text-text focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all" />
                </div>
              </div>
            </div>
            <div class="border-t border-border p-4 flex justify-end gap-2">
              <button type="button" class="px-4 py-1.5 text-sm font-medium text-text-secondary bg-bg hover:bg-border border border-border rounded transition-colors cursor-pointer" @click=${this.closeCreateModal}>取消</button>
              <button type="submit" class="px-4 py-1.5 text-sm font-medium text-white bg-primary hover:bg-primary-hover rounded transition-colors cursor-pointer shadow-sm shadow-primary/20">创建任务</button>
            </div>
          </form>
        </div>
      </div>
    `;
  }
}
